import { makeAutoObservable, reaction, toJS } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { UpdateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { tenantProfileService } from "../services/tenantProfileService";
import {
  TenantProfileValuesInit,
  getTenantProfileUpdateVariables,
  getTenantProfileValues,
  TenantProfileValuesType,
  TenantProfileErrorsType,
  tenantProfileDisplayId,
} from "./TenantProfileCommonStore";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { TenantProfile, SortOrder } from "../../../api/graphql";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getPublisherValues,
  PublisherValuesType,
  publisherDisplayId,
} from "../../publisher/store/PublisherCommonStore";
import { validateMask } from "../../common/utils";
import { normalizeAndValidateEmail } from "../../../common/utils";
import {
  getCountryValues,
  CountryValuesType,
  countryDisplayId,
} from "../../country/store/CountryCommonStore";
import {
  getProvinceStateValues,
  ProvinceStateValuesType,
  provinceStateDisplayId,
} from "../../provinceState/store/ProvinceStateCommonStore";
import {
  getVatStatusValues,
  VatStatusValuesType,
  vatStatusDisplayId,
} from "../../vatStatus/store/VatStatusCommonStore";
import {
  getGrossReceiptsStatusValues,
  GrossReceiptsStatusValuesType,
  grossReceiptsStatusDisplayId,
} from "../../grossReceiptsStatus/store/GrossReceiptsStatusCommonStore";
import {
  getCurrencyValues,
  CurrencyValuesType,
  currencyDisplayId,
} from "../../currency/store/CurrencyCommonStore";
import { PersonType } from "../../../api/graphql";
import { publisherService } from "../../publisher/services/publisherService";
import { countryService } from "../../country/services/countryService";
import { provinceStateService } from "../../provinceState/services/provinceStateService";
import { vatStatusService } from "../../vatStatus/services/vatStatusService";
import { grossReceiptsStatusService } from "../../grossReceiptsStatus/services/grossReceiptsStatusService";
import { currencyService } from "../../currency/services/currencyService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class TenantProfileUpdateStore implements UpdateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  confirmDialogOpened: boolean = false;
  confirmDialogTitle: string = "";
  confirmDialogDescription: string = "";
  confirmDialogCallback: (() => void) | null = null;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: TenantProfileErrorsType = {};
  service = tenantProfileService;
  tenantProfileInstance: TenantProfile | null = null;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
    business: { open: true, errors: [] },
    address: { open: true, errors: [] },
    billing: { open: true, errors: [] },
    printer: { open: true, errors: [] },
    integrationsSadaic: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda (envío y relectura): la capa del formulario (SavingOverlay)
  // lo bloquea, así nada de lo que se escribiera lo pisa la relectura.
  isSaving: boolean = false;
  // Se guardó, pero el formulario no se pudo poner al día (la relectura falló, o
  // el formulario cambió mientras se guardaba): Guardar queda deshabilitado hasta
  // recargar (OutdatedAfterSaveNotice), así nunca se guarda desde un estado viejo.
  savedButOutdated: boolean = false;
  // En ese estado, lo que se escribe cuenta como cambio pendiente: la foto del
  // formulario cubre los campos; este vigía, también las grillas y los archivos.
  private editedWhileOutdated: boolean = false;
  private stopOutdatedWatch?: () => void;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = TenantProfileValuesInit();
  personTypeError: string | undefined;
  legalNameError: string | undefined;
  ownPublisherError: string | undefined;
  ownPublisherService = publisherService;
  ownPublisherSearchValue: string = "";
  ownPublisherOptions: PublisherValuesType[] = [];
  businessIdError: string | undefined;
  legalEmailError: string | undefined;
  streetAndNumberError: string | undefined;
  floorError: string | undefined;
  suiteError: string | undefined;
  postalCodeError: string | undefined;
  countryError: string | undefined;
  countryService = countryService;
  countrySearchValue: string = "";
  countryOptions: CountryValuesType[] = [];
  provinceError: string | undefined;
  provinceService = provinceStateService;
  provinceSearchValue: string = "";
  provinceOptions: ProvinceStateValuesType[] = [];
  cityError: string | undefined;
  vatStatusError: string | undefined;
  vatStatusService = vatStatusService;
  vatStatusSearchValue: string = "";
  vatStatusOptions: VatStatusValuesType[] = [];
  taxIdError: string | undefined;
  grossReceiptsStatusError: string | undefined;
  grossReceiptsStatusService = grossReceiptsStatusService;
  grossReceiptsStatusSearchValue: string = "";
  grossReceiptsStatusOptions: GrossReceiptsStatusValuesType[] = [];
  grossReceiptsIdError: string | undefined;
  billingEmailError: string | undefined;
  billingCurrencyError: string | undefined;
  billingCurrencyService = currencyService;
  billingCurrencySearchValue: string = "";
  billingCurrencyOptions: CurrencyValuesType[] = [];
  printerNameError: string | undefined;
  printerStreetAndNumberError: string | undefined;
  printerFloorError: string | undefined;
  printerSuiteError: string | undefined;
  printerPostalCodeError: string | undefined;
  printerCountryError: string | undefined;
  printerCountryService = countryService;
  printerCountrySearchValue: string = "";
  printerCountryOptions: CountryValuesType[] = [];
  printerProvinceError: string | undefined;
  printerProvinceService = provinceStateService;
  printerProvinceSearchValue: string = "";
  printerProvinceOptions: ProvinceStateValuesType[] = [];
  printerCityError: string | undefined;
  sadaicFtpUrlError: string | undefined;
  sadaicFtpUserError: string | undefined;
  sadaicFtpPasswordError: string | undefined;
  sadaicEmailError: string | undefined;

  private customValidators: CustomValidator<TenantProfileValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction?: () => void;
  originalState: any = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Drawer mode flag - when true, close() pops/closes the drawer instead of navigating
  isDrawerMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: { resultId?: string; cancelled?: boolean }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();

    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<TenantProfileValuesType>
        );
      }
    });
    makeAutoObservable(this, {
      originalState: false,
    });
    this.setupChangeDetection();
  }

  destroy() {
    // Primero, lejos de lo que un proyecto agrega después del bloque de stopReaction.
    this.stopOutdatedWatch?.();
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<TenantProfileValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  *loadTenantProfileById(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    this.tenantProfileInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (this.tenantProfileInstance) {
      // Lo que se ve es lo que hay en el server: el formulario está al día.
      this.becomeCurrent();
      this.entityValues = getTenantProfileValues(
        this.tenantProfileInstance,
        this.langStore
      );
      this.saveOriginalState();
    }
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  // Guardar deshabilitado: mientras se guarda, y con el formulario guardado pero desactualizado.
  get saveDisabled() {
    return this.isProcessing || this.savedButOutdated;
  }

  /**
   * Vuelve a leer el registro: la acción del aviso de guardado pero desactualizado.
   * Si la lectura sale bien el formulario queda al día y Guardar se vuelve a
   * habilitar; si no, el error queda a la vista y el aviso sigue.
   */
  *reloadTenantProfile(): any {
    if (this.isProcessing) return;
    if (this.pendingChanges) {
      // Lo escrito después de quedar desactualizado se descartaría: se pregunta,
      // como al salir.
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.reloadWithoutSaving,
        this.executeReloadAction.bind(this)
      );
      return;
    }
    yield this.executeReloadAction();
  }

  *executeReloadAction(): any {
    yield this.process(() => this.loadTenantProfileById(this.entityValues.id));
  }

  /**
   * Queda guardado pero desactualizado. Se toma la foto del formulario: lo editado
   * antes de guardar ya se guardó y no cuenta; lo que se escriba desde ahora cuenta
   * como cambio pendiente, y Cancelar, la navegación y Recargar avisan antes de
   * descartarlo. Para lo que la foto no cubre (las grillas y los archivos), el
   * vigía lo marca.
   */
  private becomeOutdated() {
    this.savedButOutdated = true;
    this.resetPendingChanges();
    this.saveOriginalState();
    this.editedWhileOutdated = false;
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = reaction(
      () => ({ ...toJS(this.entityValues), version_: undefined }),
      () => {
        this.editedWhileOutdated = true;
      },
      { equals: (a, b) => _.isEqual(a, b) }
    );
  }

  /** El formulario está al día (se volvió a leer el registro): se sale del estado. */
  private becomeCurrent() {
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = undefined;
    this.editedWhileOutdated = false;
    this.savedButOutdated = false;
  }

  get displayId(): string {
    if (this._ownPendingChanges && this.entityValues) {
      return tenantProfileDisplayId(this.entityValues as any, this.langStore);
    }
    if (this.tenantProfileInstance) {
      return tenantProfileDisplayId(this.tenantProfileInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.TenantProfileSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "business",
        label: this.rootStore.langStore.groupTenantProfileBusinessLabel,
        errors: this.getCardErrors("business"),
      },
      {
        id: "address",
        label: this.rootStore.langStore.groupTenantProfileAddressLabel,
        errors: this.getCardErrors("address"),
      },
      {
        id: "billing",
        label: this.rootStore.langStore.groupTenantProfileBillingLabel,
        errors: this.getCardErrors("billing"),
      },
      {
        id: "printer",
        label: this.rootStore.langStore.groupTenantProfilePrinterLabel,
        errors: this.getCardErrors("printer"),
      },
      {
        id: "integrationsSadaic",
        label:
          this.rootStore.langStore.groupTenantProfileIntegrationsSadaicLabel,
        errors: this.getCardErrors("integrationsSadaic"),
      },
    ];
  }

  *returnedData(data: any): any {
    const { callerData, returnData } = data;
    if (
      callerData &&
      returnData &&
      !returnData.cancelled &&
      (returnData.entity || returnData.resultId)
    ) {
      // Delegate to subentity UpdateStore if callerData.entityName matches a subentity

      if (callerData?.fieldName === "ownPublisher") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            publisherDisplayId(returnData.entity, this.langStore);
          this.entityValues.showOwnPublisher = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.publisherOne({ id: returnData.resultId });
          const publisher = result.publisherOne;
          this.entityValues.showOwnPublisher = {
            displayId: publisher.displayId,
            id: publisher.id,
          };
          this.setOwnPublisherOptions([
            getPublisherValues(publisher, this.langStore),
          ]);
        }
      } else if (callerData?.fieldName === "country") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            countryDisplayId(returnData.entity, this.langStore);
          this.entityValues.showCountry = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.countryOne({ id: returnData.resultId });
          const country = result.countryOne;
          this.entityValues.showCountry = {
            displayId: country.displayId,
            id: country.id,
          };
          this.setCountryOptions([getCountryValues(country, this.langStore)]);
        }
      } else if (callerData?.fieldName === "province") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            provinceStateDisplayId(returnData.entity, this.langStore);
          this.entityValues.showProvince = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.provinceStateOne({
            id: returnData.resultId,
          });
          const provinceState = result.provinceStateOne;
          this.entityValues.showProvince = {
            displayId: provinceState.displayId,
            id: provinceState.id,
          };
          this.setProvinceOptions([
            getProvinceStateValues(provinceState, this.langStore),
          ]);
        }
      } else if (callerData?.fieldName === "vatStatus") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            vatStatusDisplayId(returnData.entity, this.langStore);
          this.entityValues.showVatStatus = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.vatStatusOne({ id: returnData.resultId });
          const vatStatus = result.vatStatusOne;
          this.entityValues.showVatStatus = {
            displayId: vatStatus.displayId,
            id: vatStatus.id,
          };
          this.setVatStatusOptions([
            getVatStatusValues(vatStatus, this.langStore),
          ]);
        }
      } else if (callerData?.fieldName === "grossReceiptsStatus") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            grossReceiptsStatusDisplayId(returnData.entity, this.langStore);
          this.entityValues.showGrossReceiptsStatus = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.grossReceiptsStatusOne({
            id: returnData.resultId,
          });
          const grossReceiptsStatus = result.grossReceiptsStatusOne;
          this.entityValues.showGrossReceiptsStatus = {
            displayId: grossReceiptsStatus.displayId,
            id: grossReceiptsStatus.id,
          };
          this.setGrossReceiptsStatusOptions([
            getGrossReceiptsStatusValues(grossReceiptsStatus, this.langStore),
          ]);
        }
      } else if (callerData?.fieldName === "billingCurrency") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            currencyDisplayId(returnData.entity, this.langStore);
          this.entityValues.showBillingCurrency = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.currencyOne({ id: returnData.resultId });
          const currency = result.currencyOne;
          this.entityValues.showBillingCurrency = {
            displayId: currency.displayId,
            id: currency.id,
          };
          this.setBillingCurrencyOptions([
            getCurrencyValues(currency, this.langStore),
          ]);
        }
      } else if (callerData?.fieldName === "printerCountry") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            countryDisplayId(returnData.entity, this.langStore);
          this.entityValues.showPrinterCountry = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.countryOne({ id: returnData.resultId });
          const country = result.countryOne;
          this.entityValues.showPrinterCountry = {
            displayId: country.displayId,
            id: country.id,
          };
          this.setPrinterCountryOptions([
            getCountryValues(country, this.langStore),
          ]);
        }
      } else if (callerData?.fieldName === "printerProvince") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            provinceStateDisplayId(returnData.entity, this.langStore);
          this.entityValues.showPrinterProvince = {
            id: returnData.entity.id,
            displayId,
          };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.provinceStateOne({
            id: returnData.resultId,
          });
          const provinceState = result.provinceStateOne;
          this.entityValues.showPrinterProvince = {
            displayId: provinceState.displayId,
            id: provinceState.id,
          };
          this.setPrinterProvinceOptions([
            getProvinceStateValues(provinceState, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  showConfirmDialog(title: string, description: string, callback: () => void) {
    this.confirmDialogTitle = title;
    this.confirmDialogDescription = description;
    this.confirmDialogCallback = callback;
    this.confirmDialogOpened = true;
  }

  closeConfirmDialog() {
    this.confirmDialogOpened = false;
    this.confirmDialogCallback = null;
  }

  *executeConfirmedAction() {
    const callback = this.confirmDialogCallback;
    this.closeConfirmDialog();
    if (callback) {
      yield callback();
    }
  }

  getPersonTypeValue(): PersonType | "" {
    return this.entityValues.personType;
  }

  setPersonTypeValue(value: PersonType | "") {
    this.entityValues.personType = value;
  }

  getPersonTypeOptions() {
    return this.langStore.PersonTypeOptions;
  }

  getLegalNameValue(): string {
    return this.entityValues.legalName;
  }

  setLegalNameValue(value: string) {
    this.entityValues.legalName = value;
  }

  setOwnPublisherOptions(values: PublisherValuesType[]) {
    this.ownPublisherOptions = values;
  }

  *loadOwnPublisherValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Publisher");
    const allOptions = cache.getOptions("Publisher") as PublisherValuesType[];

    const searchValue = this.ownPublisherSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setOwnPublisherOptions(filtered);
  }

  getOwnPublisherOptions() {
    return this.ownPublisherOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setOwnPublisherValue(value: string) {
    this.ownPublisherSearchValue = value;
    yield this.loadOwnPublisherValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedOwnPublisher(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewPublisher(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "ownPublisher"
      );
    } else {
      if (value) {
        this.entityValues.showOwnPublisher = value;
        this.entityValues.__ownPublisher = value.displayId;
      }
    }
  }

  getBusinessIdValue(): string {
    return this.entityValues.businessId;
  }

  setBusinessIdValue(value: string) {
    this.entityValues.businessId = value;
  }

  getLegalEmailValue(): string {
    return this.entityValues.legalEmail;
  }

  setLegalEmailValue(value: string) {
    this.entityValues.legalEmail = value;
  }

  getStreetAndNumberValue(): string {
    return this.entityValues.streetAndNumber;
  }

  setStreetAndNumberValue(value: string) {
    this.entityValues.streetAndNumber = value;
  }

  getFloorValue(): string {
    return this.entityValues.floor;
  }

  setFloorValue(value: string) {
    this.entityValues.floor = value;
  }

  getSuiteValue(): string {
    return this.entityValues.suite;
  }

  setSuiteValue(value: string) {
    this.entityValues.suite = value;
  }

  getPostalCodeValue(): string {
    return this.entityValues.postalCode;
  }

  setPostalCodeValue(value: string) {
    this.entityValues.postalCode = value;
  }

  setCountryOptions(values: CountryValuesType[]) {
    this.countryOptions = values;
  }

  *loadCountryValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Country");
    const allOptions = cache.getOptions("Country") as CountryValuesType[];

    const searchValue = this.countrySearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setCountryOptions(filtered);
  }

  getCountryOptions() {
    return this.countryOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setCountryValue(value: string) {
    this.countrySearchValue = value;
    yield this.loadCountryValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedCountry(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewCountry(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "country"
      );
    } else {
      if (value) {
        this.entityValues.showCountry = value;
        this.entityValues.__country = value.displayId;
        yield this.loadProvinceValues();
        yield this.loadPrinterProvinceValues();
        this.entityValues.showProvince = null;
        this.entityValues.__province = "";
        this.entityValues.showPrinterProvince = null;
        this.entityValues.__printerProvince = "";
      }
    }
  }

  setProvinceOptions(values: ProvinceStateValuesType[]) {
    this.provinceOptions = values;
  }

  *loadProvinceValues(): any {
    const parentId = this.entityValues.showCountry?.id;
    if (parentId) {
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.provinceStateListWithOffset({
        offset: 0,
        take: 100,
        where: { country: { id: { equals: parentId } } },
        orderBy: [{ [this.langStore.fieldByLang("name")]: SortOrder.Asc }],
      });
      this.setProvinceOptions(
        (result.provinceStateListWithOffset || []).map((x: any) =>
          getProvinceStateValues(x, this.langStore)
        )
      );
    } else {
      this.setProvinceOptions([]);
    }
  }

  getProvinceOptions() {
    return this.provinceOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setProvinceValue(value: string) {
    this.provinceSearchValue = value;
    yield this.loadProvinceValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedProvince(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewProvinceState(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "province"
      );
    } else {
      if (value) {
        this.entityValues.showProvince = value;
        this.entityValues.__province = value.displayId;
      }
    }
  }

  getCityValue(): string {
    return this.entityValues.city;
  }

  setCityValue(value: string) {
    this.entityValues.city = value;
  }

  setVatStatusOptions(values: VatStatusValuesType[]) {
    this.vatStatusOptions = values;
  }

  *loadVatStatusValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("VatStatus");
    const allOptions = cache.getOptions("VatStatus") as VatStatusValuesType[];

    const searchValue = this.vatStatusSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setVatStatusOptions(filtered);
  }

  getVatStatusOptions() {
    return this.vatStatusOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setVatStatusValue(value: string) {
    this.vatStatusSearchValue = value;
    yield this.loadVatStatusValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedVatStatus(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewVatStatus(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "vatStatus"
      );
    } else {
      if (value) {
        this.entityValues.showVatStatus = value;
        this.entityValues.__vatStatus = value.displayId;
      }
    }
  }

  getTaxIdValue(): string {
    return this.entityValues.taxId;
  }

  setTaxIdValue(value: string) {
    this.entityValues.taxId = value;
  }

  setGrossReceiptsStatusOptions(values: GrossReceiptsStatusValuesType[]) {
    this.grossReceiptsStatusOptions = values;
  }

  *loadGrossReceiptsStatusValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("GrossReceiptsStatus");
    const allOptions = cache.getOptions(
      "GrossReceiptsStatus"
    ) as GrossReceiptsStatusValuesType[];

    const searchValue =
      this.grossReceiptsStatusSearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setGrossReceiptsStatusOptions(filtered);
  }

  getGrossReceiptsStatusOptions() {
    return this.grossReceiptsStatusOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setGrossReceiptsStatusValue(value: string) {
    this.grossReceiptsStatusSearchValue = value;
    yield this.loadGrossReceiptsStatusValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedGrossReceiptsStatus(
    value: { displayId: string; id: string } | null
  ) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewGrossReceiptsStatus(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "grossReceiptsStatus"
      );
    } else {
      if (value) {
        this.entityValues.showGrossReceiptsStatus = value;
        this.entityValues.__grossReceiptsStatus = value.displayId;
      }
    }
  }

  getGrossReceiptsIdValue(): string {
    return this.entityValues.grossReceiptsId;
  }

  setGrossReceiptsIdValue(value: string) {
    this.entityValues.grossReceiptsId = value;
  }

  getBillingEmailValue(): string {
    return this.entityValues.billingEmail;
  }

  setBillingEmailValue(value: string) {
    this.entityValues.billingEmail = value;
  }

  setBillingCurrencyOptions(values: CurrencyValuesType[]) {
    this.billingCurrencyOptions = values;
  }

  *loadBillingCurrencyValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Currency");
    const allOptions = cache.getOptions("Currency") as CurrencyValuesType[];

    const searchValue = this.billingCurrencySearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setBillingCurrencyOptions(filtered);
  }

  getBillingCurrencyOptions() {
    return this.billingCurrencyOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setBillingCurrencyValue(value: string) {
    this.billingCurrencySearchValue = value;
    yield this.loadBillingCurrencyValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedBillingCurrency(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewCurrency(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "billingCurrency"
      );
    } else {
      if (value) {
        this.entityValues.showBillingCurrency = value;
        this.entityValues.__billingCurrency = value.displayId;
      }
    }
  }

  getPrinterNameValue(): string {
    return this.entityValues.printerName;
  }

  setPrinterNameValue(value: string) {
    this.entityValues.printerName = value;
  }

  getPrinterStreetAndNumberValue(): string {
    return this.entityValues.printerStreetAndNumber;
  }

  setPrinterStreetAndNumberValue(value: string) {
    this.entityValues.printerStreetAndNumber = value;
  }

  getPrinterFloorValue(): string {
    return this.entityValues.printerFloor;
  }

  setPrinterFloorValue(value: string) {
    this.entityValues.printerFloor = value;
  }

  getPrinterSuiteValue(): string {
    return this.entityValues.printerSuite;
  }

  setPrinterSuiteValue(value: string) {
    this.entityValues.printerSuite = value;
  }

  getPrinterPostalCodeValue(): string {
    return this.entityValues.printerPostalCode;
  }

  setPrinterPostalCodeValue(value: string) {
    this.entityValues.printerPostalCode = value;
  }

  setPrinterCountryOptions(values: CountryValuesType[]) {
    this.printerCountryOptions = values;
  }

  *loadPrinterCountryValues(): any {
    const cache = this.rootStore.referenceCacheStore;
    yield cache.ensureLoaded("Country");
    const allOptions = cache.getOptions("Country") as CountryValuesType[];

    const searchValue = this.printerCountrySearchValue?.toLowerCase() || "";
    let filtered = searchValue
      ? allOptions.filter((item: any) =>
          item.displayId.toLowerCase().includes(searchValue)
        )
      : [...allOptions];

    filtered.sort((a: any, b: any) => a.displayId.localeCompare(b.displayId));
    this.setPrinterCountryOptions(filtered);
  }

  getPrinterCountryOptions() {
    return this.printerCountryOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setPrinterCountryValue(value: string) {
    this.printerCountrySearchValue = value;
    yield this.loadPrinterCountryValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedPrinterCountry(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewCountry(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "printerCountry"
      );
    } else {
      if (value) {
        this.entityValues.showPrinterCountry = value;
        this.entityValues.__printerCountry = value.displayId;
      }
    }
  }

  setPrinterProvinceOptions(values: ProvinceStateValuesType[]) {
    this.printerProvinceOptions = values;
  }

  *loadPrinterProvinceValues(): any {
    const parentId = this.entityValues.showCountry?.id;
    if (parentId) {
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.provinceStateListWithOffset({
        offset: 0,
        take: 100,
        where: { country: { id: { equals: parentId } } },
        orderBy: [{ [this.langStore.fieldByLang("name")]: SortOrder.Asc }],
      });
      this.setPrinterProvinceOptions(
        (result.provinceStateListWithOffset || []).map((x: any) =>
          getProvinceStateValues(x, this.langStore)
        )
      );
    } else {
      this.setPrinterProvinceOptions([]);
    }
  }

  getPrinterProvinceOptions() {
    return this.printerProvinceOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setPrinterProvinceValue(value: string) {
    this.printerProvinceSearchValue = value;
    yield this.loadPrinterProvinceValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedPrinterProvince(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewProvinceState(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "printerProvince"
      );
    } else {
      if (value) {
        this.entityValues.showPrinterProvince = value;
        this.entityValues.__printerProvince = value.displayId;
      }
    }
  }

  getPrinterCityValue(): string {
    return this.entityValues.printerCity;
  }

  setPrinterCityValue(value: string) {
    this.entityValues.printerCity = value;
  }

  getSadaicFtpUrlValue(): string {
    return this.entityValues.sadaicFtpUrl;
  }

  setSadaicFtpUrlValue(value: string) {
    this.entityValues.sadaicFtpUrl = value;
  }

  getSadaicFtpUserValue(): string {
    return this.entityValues.sadaicFtpUser;
  }

  setSadaicFtpUserValue(value: string) {
    this.entityValues.sadaicFtpUser = value;
  }

  getSadaicFtpPasswordValue(): string {
    return this.entityValues.sadaicFtpPassword;
  }

  setSadaicFtpPasswordValue(value: string) {
    this.entityValues.sadaicFtpPassword = value;
  }

  getSadaicEmailValue(): string {
    return this.entityValues.sadaicEmail;
  }

  setSadaicEmailValue(value: string) {
    this.entityValues.sadaicEmail = value;
  }

  addNewPublisher(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "TenantProfile",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/publisher/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewCountry(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "TenantProfile",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/country/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewProvinceState(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "TenantProfile",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/provinceState/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewVatStatus(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "TenantProfile",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/vatStatus/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewGrossReceiptsStatus(
    notFound: string | undefined,
    callerFieldName?: string
  ) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "TenantProfile",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/grossReceiptsStatus/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  addNewCurrency(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "TenantProfile",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/currency/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.tenantProfileInstance = null;
    this.entityValues = TenantProfileValuesInit();
    this.cardState = {
      null: { open: true, errors: [] },
      business: { open: true, errors: [] },
      address: { open: true, errors: [] },
      billing: { open: true, errors: [] },
      printer: { open: true, errors: [] },
      integrationsSadaic: { open: true, errors: [] },
    };
    this.ownPublisherSearchValue = "";
    this.ownPublisherOptions = [];
    this.countrySearchValue = "";
    this.countryOptions = [];
    this.provinceSearchValue = "";
    this.provinceOptions = [];
    this.vatStatusSearchValue = "";
    this.vatStatusOptions = [];
    this.grossReceiptsStatusSearchValue = "";
    this.grossReceiptsStatusOptions = [];
    this.billingCurrencySearchValue = "";
    this.billingCurrencyOptions = [];
    this.printerCountrySearchValue = "";
    this.printerCountryOptions = [];
    this.printerProvinceSearchValue = "";
    this.printerProvinceOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: TenantProfileValuesType): TenantProfileErrorsType {
    const errors: TenantProfileErrorsType = {};
    this.clearErrors();
    if (values.businessId && true) {
      if (
        !["99-9999999-9", "99-99999999-9"].some((m) =>
          validateMask(values.businessId, { mask: m })
        )
      ) {
        errors.businessId = {
          message: this.langStore.maskedField("99-9999999-9 | 99-99999999-9"),
          fieldLabel: this.langStore.TenantProfileBusinessIdLabel,
        };
      }
    }

    if (values.legalEmail) {
      const normalizedEmail = normalizeAndValidateEmail(values.legalEmail);
      if (normalizedEmail === null) {
        errors.legalEmail = {
          message: this.langStore.badSyntaxEmail,
          fieldLabel: this.langStore.TenantProfileLegalEmailLabel,
        };
        this.setCardError("business", errors.legalEmail.message);
      }
    }

    if (values.billingEmail) {
      const normalizedEmail = normalizeAndValidateEmail(values.billingEmail);
      if (normalizedEmail === null) {
        errors.billingEmail = {
          message: this.langStore.badSyntaxEmail,
          fieldLabel: this.langStore.TenantProfileBillingEmailLabel,
        };
        this.setCardError("billing", errors.billingEmail.message);
      }
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined,
      "TenantProfileUpdateStore"
    );
  }

  close(returnData?: { resultId?: string; cancelled?: boolean }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.isDrawerMode) {
      // In drawer mode, close or pop the drawer instead of navigating
      if (this.rootStore.drawerViewStack.length > 1) {
        this.rootStore.popDrawerView();
      } else {
        this.rootStore.closeDrawerView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else {
      this.rootStore.router.goBack();
    }
  }

  get pendingChanges(): boolean {
    // Guardado pero desactualizado: cuenta lo escrito desde que se entró en ese
    // estado (la foto, y el vigía para lo que la foto no cubre), no las filas y
    // los archivos que ya se guardaron.
    if (this.savedButOutdated)
      return this._ownPendingChanges || this.editedWhileOutdated;
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      personType: this.entityValues.personType,
      legalName: this.entityValues.legalName,
      businessId: this.entityValues.businessId,
      legalEmail: this.entityValues.legalEmail,
      streetAndNumber: this.entityValues.streetAndNumber,
      floor: this.entityValues.floor,
      suite: this.entityValues.suite,
      postalCode: this.entityValues.postalCode,
      city: this.entityValues.city,
      taxId: this.entityValues.taxId,
      grossReceiptsId: this.entityValues.grossReceiptsId,
      billingEmail: this.entityValues.billingEmail,
      printerName: this.entityValues.printerName,
      printerStreetAndNumber: this.entityValues.printerStreetAndNumber,
      printerFloor: this.entityValues.printerFloor,
      printerSuite: this.entityValues.printerSuite,
      printerPostalCode: this.entityValues.printerPostalCode,
      printerCity: this.entityValues.printerCity,
      sadaicFtpUrl: this.entityValues.sadaicFtpUrl,
      sadaicFtpUser: this.entityValues.sadaicFtpUser,
      sadaicFtpPassword: this.entityValues.sadaicFtpPassword,
      sadaicEmail: this.entityValues.sadaicEmail,
      ownPublisher: this.entityValues.showOwnPublisher?.id || null,
      country: this.entityValues.showCountry?.id || null,
      province: this.entityValues.showProvince?.id || null,
      vatStatus: this.entityValues.showVatStatus?.id || null,
      grossReceiptsStatus:
        this.entityValues.showGrossReceiptsStatus?.id || null,
      billingCurrency: this.entityValues.showBillingCurrency?.id || null,
      printerCountry: this.entityValues.showPrinterCountry?.id || null,
      printerProvince: this.entityValues.showPrinterProvince?.id || null,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  resetPendingChanges() {
    this._ownPendingChanges = false;
  }

  getChangedFields(): string[] {
    if (!this.pendingChanges) return [];

    const changed: string[] = [];

    if (this._ownPendingChanges && this.originalSnapshot) {
      const current = this.computeSnapshot();

      Object.keys(current).forEach((key) => {
        if (!_.isEqual(current[key], this.originalSnapshot![key])) {
          changed.push(key);
        }
      });
    }

    return changed;
  }

  *saveTenantProfile(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: el primero
    // cambia la versión del registro, y el segundo fallaría con RECORD_MODIFIED
    // aunque los datos ya estén guardados. Tampoco con el formulario
    // guardado pero desactualizado: no se guarda desde un estado viejo.
    if (this.saveDisabled) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que haga correr la relectura y el
      // cierre como si éste hubiera salido bien.
      this.savedId = null;
      // Mientras se guarda, la capa no deja editar. Si el formulario cambia igual
      // (algún camino programático), lo que se ve ya no es lo que se guardó, y no
      // se concilia nada: queda guardado pero desactualizado, como si la relectura
      // fallara. La versión nueva, que _save toma de la respuesta, no cuenta.
      let changedWhileSaving = false;
      const stopWatching = reaction(
        () => ({ ...toJS(this.entityValues), version_: undefined }),
        () => {
          changedWhileSaving = true;
        },
        { equals: (a, b) => _.isEqual(a, b) }
      );
      // Desde acá hasta que termina (también la relectura), el formulario está
      // bloqueado por su capa. Pase lo que pase (también si el manejo del error
      // tira), el finally de afuera lo libera.
      this.isSaving = true;
      try {
        try {
          this.status = "processing";
          yield this._save();
        } catch (error) {
          this.status = "error";

          if (error instanceof Error) {
            const apiError = getApiError(error, this.langStore);
            this.showErrorDialog(
              apiError.code,
              apiError.title,
              apiError.message
            );
          } else {
            this.showErrorDialog(this.langStore.serverError, undefined);
          }
        } finally {
          stopWatching();
        }

        if (!this.savedId) {
          // Sin guardado exitoso (falló, o no había nada que guardar): Guardar se
          // vuelve a habilitar.
          if (this.isProcessing) this.status = "ok";
        } else {
          try {
            // El formulario queda con el registro del server, no con lo que mandó:
            // las filas nuevas toman su id, los archivos quedan guardados (no
            // "added", que se volverían a mandar) y version_ es el que quedó. Si el
            // formulario sigue abierto (volver atrás no navega, por ejemplo en una
            // pestaña nueva), el próximo guardado parte de ahí. Si no se puede poner
            // al día (cambió mientras se guardaba, o la relectura falla), el guardado
            // ya está hecho, pero lo que se ve no es lo que hay: queda guardado pero
            // desactualizado, con Guardar deshabilitado y el aviso con Recargar, y
            // no se cierra.
            if (changedWhileSaving) {
              this.becomeOutdated();
            } else {
              try {
                yield this.loadTenantProfileById(this.savedId);
              } catch (error) {
                console.warn(
                  "[TenantProfileUpdateStore] saved, but reloading the record failed:",
                  error
                );
                this.becomeOutdated();
              }
            }
            if (!this.savedButOutdated) {
              this.resetPendingChanges();
              this.saveOriginalState();
            }
          } finally {
            // Hasta acá sigue guardando (isProcessing): Guardar deshabilitado, y un
            // segundo guardado no corre mientras se relee.
            this.status = "ok";
          }
          if (!this.savedButOutdated)
            this.close({ resultId: this.savedId, cancelled: false });
        }
      } finally {
        // Pase lo que pase (también si el manejo del error tira), el formulario
        // se libera: la capa se cierra.
        this.isSaving = false;
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  *cancelTenantProfile(): any {
    if (this.pendingChanges) {
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.exitWithoutSaving,
        this.executeCancelAction.bind(this)
      );
      return;
    }
    yield this.executeCancelAction();
  }

  executeCancelAction(): void {
    this.cancelled = true;
    this._ownPendingChanges = false;

    this.close({ cancelled: true });
  }

  getState(): InitializeParams {
    return {
      id: this.entityValues.id,
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getTenantProfileValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = TenantProfileValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
    this.saveOriginalState();
  }

  *_save(): any {
    if (this.tenantProfileInstance) {
      const variables = getTenantProfileUpdateVariables(this.entityValues);
      this.savedId = null;
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.tenantProfileUpdate({
        ...variables,
        idempotencyKey_: this.idempotencyKeyFor(variables),
      });
      this.savedId = result.tenantProfileUpdate.id;
      // La versión nueva la da el server: si el formulario sigue abierto (volver
      // atrás no navega, por ejemplo en una pestaña nueva), el próximo guardado
      // la manda y no choca consigo mismo.
      this.entityValues.version_ = result.tenantProfileUpdate.version_;
      this.saveAttempt = null;
      this.tenantProfileInstance = result.tenantProfileUpdate as TenantProfile;
    }
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const idToLoad = params?.id || params?.duplicateFromId;

      if (idToLoad) {
        yield this.loadTenantProfileById(idToLoad);

        if (params?.duplicateFromId) {
          this.entityValues.id = "";
        }
      }

      if (params?.callbackData?.filter) {
        Object.assign(this.entityValues, params.callbackData.filter);
      }

      if (params?.uiState?.cardState) {
        Object.assign(this.cardState, params.uiState.cardState);
      }

      if (params?.preloadedData) {
        Object.assign(this.entityValues, params.preloadedData);
      }

      yield this.loadOwnPublisherValues();
      yield this.loadCountryValues();
      yield this.loadProvinceValues();
      yield this.loadVatStatusValues();
      yield this.loadGrossReceiptsStatusValues();
      yield this.loadBillingCurrencyValues();
      yield this.loadPrinterCountryValues();
      yield this.loadPrinterProvinceValues();
      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
